// ─────────────────────────────────────────────────────────────────────────────
// components/Spinner.tsx — THE WAIT, ONCE.
//
// "Wherever there is a wait, show the logo turning so the user knows to wait"
// (Buds, 6 Sep 2026). A blank pause and a broken page look identical; a turning
// mark says the machine has the click and is working.
//
// Inline (in a button): the M glyph turning in CURRENTCOLOR, so it always
// matches the button's own text — white on a dark button, ink on a light one —
// with no per-button tone to get wrong. Block (a whole page/panel holding):
// the full colour tile, centred.
// ─────────────────────────────────────────────────────────────────────────────

import { Logo, LogoLetter, type LogoTone } from "./Logo";

export function Spinner({
  size = 16,
  label,
  className = "",
}: {
  size?: number;
  /** Optional word set beside the mark — a button keeps its meaning while it waits. */
  label?: string;
  className?: string;
  /** Accepted for source compatibility but ignored: the inline mark inherits the
   *  surrounding text colour (currentColor), so it never needs a tone. */
  tone?: LogoTone;
}) {
  return (
    <span
      className={`inline-flex items-center gap-2 align-[-3px] ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <LogoLetter size={size} className="animate-spin motion-reduce:animate-none" />
      {label ? <span>{label}</span> : <span className="sr-only">Working…</span>}
    </span>
  );
}

/** A whole section or page holding — the full colour mark, centred, a line under it. */
export function SpinnerBlock({
  label = "Working…",
  tone = "ink",
  size = 40,
  className = "",
}: {
  label?: string;
  tone?: LogoTone;
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 py-10 text-center ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Logo size={size} tone={tone} className="animate-spin motion-reduce:animate-none" />
      <p className="text-sm text-stone-600">{label}</p>
    </div>
  );
}
